import { Config } from '@/config';
import { apiFetch } from '@/services/localMode';
import type { BackendUser, PasskeyResponse, HealthResponse, SubscriptionInfo, CreatorConfig } from '@/types';

const BASE = Config.BACKEND_URL;

// ─── helpers ───

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, `HTTP ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

/**
 * The /api/users/profile and /api/users/register endpoints return
 * circular JSON: user → videos[] → user → videos[] → …
 * The server serialises this recursively until it silently produces
 * invalid JSON.  We read the body as text, strip nested "user" objects
 * from inside video entries (they're redundant we already have the
 * top-level user), and parse the cleaned result.
 */
async function jsonUser(res: Response): Promise<BackendUser> {
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, `HTTP ${res.status}: ${body}`);
  }

  const text = await res.text();

  // 1. Fast path if the JSON is valid, just return it.
  try {
    return JSON.parse(text) as BackendUser;
  } catch {
    // continue to sanitise
  }

  // 2. Iteratively strip the innermost "user":{…} blocks (no nested braces)
  //    until no more remain. This collapses the circular chain from the inside out.
  let cleaned = text;
  let prev = '';
  while (prev !== cleaned) {
    prev = cleaned;
    // Remove ,"user":{<no nested braces>}  or  "user":{<no nested braces>},
    cleaned = cleaned.replace(/,\s*"user"\s*:\s*\{[^{}]*\}/g, '');
    cleaned = cleaned.replace(/"user"\s*:\s*\{[^{}]*\}\s*,?/g, '');
  }

  // 3. Try parsing the cleaned version.
  try {
    return JSON.parse(cleaned) as BackendUser;
  } catch {
    // continue to fallback
  }

  // 4. Last resort regex-extract the top-level fields we actually need.
  const str  = (k: string): string | null => {
    const m = text.match(new RegExp(`"${k}"\\s*:\\s*"([^"]*)"`));
    return m ? m[1] : null;
  };
  const num  = (k: string): number => {
    const m = text.match(new RegExp(`"${k}"\\s*:\\s*(\\d+)`));
    return m ? parseInt(m[1], 10) : 0;
  };

  return {
    id: num('id'),
    username: str('username') ?? '',
    email: str('email') ?? '',
    channelId: str('channelId'),
    channelCustomId: str('channelCustomId'),
    channelName: str('channelName'),
    subscriptionLevel: str('subscriptionLevel') ?? 'FREE',
    passkey: str('passkey') ?? '',
    createdAt: str('createdAt') ?? '',
    updatedAt: str('updatedAt') ?? '',
  };
}

function jwtHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ─── public endpoints ───

export async function healthCheck(): Promise<HealthResponse> {
  const res = await apiFetch(`${BASE}/api/public/health`);
  return json<HealthResponse>(res);
}

// ─── user endpoints (JWT) ───

/**
 * Register the user with YouTube channel integration.
 * Requires both the Google ID token (JWT) and a YouTube access token.
 * Returns the full user object including the generated passkey.
 */
export async function register(
  idToken: string,
  youtubeAccessToken: string,
): Promise<BackendUser> {
  const res = await apiFetch(
    `${BASE}/api/users/register?access_token=${encodeURIComponent(youtubeAccessToken)}`,
    {
      method: 'POST',
      headers: jwtHeaders(idToken),
    },
  );
  return jsonUser(res);
}

/**
 * Get user profile from the backend (login check).
 * Returns 404 if user has not registered yet.
 */
export async function getProfile(idToken: string): Promise<BackendUser> {
  const res = await apiFetch(`${BASE}/api/users/profile`, {
    headers: jwtHeaders(idToken),
  });
  return jsonUser(res);
}

/**
 * Refresh the user's passkey.
 */
export async function refreshPasskey(idToken: string): Promise<PasskeyResponse> {
  const res = await apiFetch(`${BASE}/api/users/refresh-passkey`, {
    method: 'POST',
    headers: jwtHeaders(idToken),
  });
  return json<PasskeyResponse>(res);
}

/**
 * Get the current user's subscription details.
 */
export async function getSubscription(idToken: string): Promise<SubscriptionInfo> {
  const res = await apiFetch(`${BASE}/api/users/subscription`, {
    headers: jwtHeaders(idToken),
  });
  return json<SubscriptionInfo>(res);
}

/**
 * Cancel the current user's subscription at the end of the billing period.
 */
export async function cancelSubscription(idToken: string): Promise<{ status: string; message: string }> {
  const res = await apiFetch(`${BASE}/api/users/subscription/cancel`, {
    method: 'POST',
    headers: jwtHeaders(idToken),
  });
  return json<{ status: string; message: string }>(res);
}

// ─── creator branding (JWT) ───

/**
 * Get the signed-in creator's branding + monetization configuration.
 * The backend always returns a complete (possibly empty) object.
 */
export async function getBranding(idToken: string): Promise<CreatorConfig> {
  const res = await apiFetch(`${BASE}/api/users/branding`, {
    headers: jwtHeaders(idToken),
  });
  return json<CreatorConfig>(res);
}

/**
 * Save the creator's branding + monetization configuration.
 * A 400 carries a creator-facing validation message in the body.
 */
export async function saveBranding(
  idToken: string,
  config: CreatorConfig,
): Promise<CreatorConfig> {
  const res = await apiFetch(`${BASE}/api/users/branding`, {
    method: 'PUT',
    headers: jwtHeaders(idToken),
    body: JSON.stringify(config),
  });

  if (res.status === 400) {
    const body = await res.text();
    let message = 'Please check your branding details.';
    try {
      message = (JSON.parse(body) as { message?: string }).message ?? message;
    } catch {
      // Non-JSON body — keep the generic message.
    }
    throw new ApiError(400, message);
  }

  return json<CreatorConfig>(res);
}
